import { createSelector } from 'reselect'
import { immer } from 'zustand/middleware/immer'

import { pages } from '~/app/routes'

import { StoreState } from '../store'
import { BaseFilter, createFilterSliceActions, FilterSliceActions } from './types'

export const surgeonSchedulerLocationFilters = ['practitioner', 'location'] as const
export const surgeonSchedulerPractitionerFilters = ['practitioner', 'status'] as const
export const surgeonSchedulerAllocationFilters = ['location'] as const

export type SurgeonSchedulerFilterType = (typeof surgeonSchedulerLocationFilters)[number] | (typeof surgeonSchedulerPractitionerFilters)[number]

export type SurgeonSchedulerFilter = {
    type: SurgeonSchedulerFilterType
} & BaseFilter

type State = {
    filters: SurgeonSchedulerFilter[]
}

type Actions = {
    actions: FilterSliceActions<SurgeonSchedulerFilterType>
}

export type SurgeonSchedulerFilterSlice = {
    surgeonSchedulerFilters: State & Actions
}

const initialState: State = {
    filters: [],
}

export const createSurgeonSchedulerFilterSlice = immer<SurgeonSchedulerFilterSlice>((set, get) => ({
    surgeonSchedulerFilters: {
        ...initialState,
        actions: createFilterSliceActions<SurgeonSchedulerFilterType, SurgeonSchedulerFilter>('surgeonSchedulerFilters', set, get),
    },
}))

const selectAllFilters = (state: StoreState) => state.surgeonSchedulerFilters.filters

// View specific selector
export const selectSurgeonSchedulerFiltersForView = createSelector(
    selectAllFilters,
    (state: StoreState) => state.app.activeViews.SURGEON_SCHEDULER,
    (filters, activeView) =>
        filters.filter((filter: SurgeonSchedulerFilter) => {
            if (activeView === pages.SURGEON_SCHEDULER.views.locations.path) {
                return surgeonSchedulerLocationFilters.includes(filter.type)
            } else if (activeView === pages.SURGEON_SCHEDULER.views.allocations.path) {
                // The allocations table is filtered on rooms, the only filter its pane offers.
                return surgeonSchedulerAllocationFilters.includes(filter.type)
            } else {
                return surgeonSchedulerPractitionerFilters.includes(filter.type)
            }
        })
)

// Filter specific selectors
export const selectPractitionerValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: SurgeonSchedulerFilter) => filter.type === 'practitioner').map((filter: SurgeonSchedulerFilter) => filter.value)
)

export const selectLocationValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: SurgeonSchedulerFilter) => filter.type === 'location').map((filter: SurgeonSchedulerFilter) => filter.value)
)

export const selectStatusValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: SurgeonSchedulerFilter) => filter.type === 'status').map((filter: SurgeonSchedulerFilter) => filter.value)
)
